export function useCampaignNaming() {
  
  /**
   * Generates a campaign name based on organization settings and email data
   */
  function generateCampaignName(email: EmailT, group: EmailGroupT): string {
    // Use deploy date if available, otherwise fall back to current date
    const deployDate = email.deployDate ? new Date(email.deployDate) : new Date()
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    const currentMonth = monthNames[deployDate.getMonth()]
    const currentYear = deployDate.getFullYear().toString()
    
    // Default configuration
    const defaultConfig = {
      ecommerceSuffix: 'E-Commerce',
      newsletterSuffix: 'Newsletter',
      ecommerceKeywords: ['ecomm', 'e-comm', 'commerce', 'shop', 'store', 'sale', 'promo'],
      template: '{subject} - {month} {year} - {type}'
    }
    
    // Use organization settings or defaults
    const config = {
      ...defaultConfig,
      ...group.campaignNaming
    }
    
    // Detect email type based on alias
    const emailAlias = email.alias.toLowerCase()
    const isEcommerce = config.ecommerceKeywords.some(keyword => 
      emailAlias.includes(keyword.toLowerCase())
    )
    
    const emailType = isEcommerce ? config.ecommerceSuffix : config.newsletterSuffix
    
    // Generate campaign name using template
    const campaignName = config.template
      .replace('{subject}', email.subject || email.name || email.alias)
      .replace('{month}', currentMonth)
      .replace('{year}', currentYear)
      .replace('{type}', emailType)
    
    return campaignName
  }
  
  /**
   * Gets the default campaign naming configuration for display/editing
   */
  function getDefaultCampaignConfig() {
    return {
      ecommerceSuffix: 'E-Commerce',
      newsletterSuffix: 'Newsletter',
      ecommerceKeywords: ['ecomm', 'e-comm', 'commerce', 'shop', 'store', 'sale', 'promo'],
      template: '{subject} - {month} {year} - {type}'
    }
  }
  
  /**
   * Validates and sanitizes campaign naming configuration
   */
  function validateCampaignConfig(config: any) {
    const defaults = getDefaultCampaignConfig()
    
    return {
      ecommerceSuffix: config.ecommerceSuffix || defaults.ecommerceSuffix,
      newsletterSuffix: config.newsletterSuffix || defaults.newsletterSuffix,
      ecommerceKeywords: Array.isArray(config.ecommerceKeywords) 
        ? config.ecommerceKeywords.filter(k => typeof k === 'string' && k.trim())
        : defaults.ecommerceKeywords,
      template: config.template || defaults.template
    }
  }
  
  /**
   * Preview what a campaign name would look like with given settings
   */
  function previewCampaignName(
    subject: string, 
    emailAlias: string, 
    config: typeof getDefaultCampaignConfig,
    deployDate?: string
  ): string {
    const date = deployDate ? new Date(deployDate) : new Date()
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]
    const currentMonth = monthNames[date.getMonth()]
    const currentYear = date.getFullYear().toString()
    
    const emailAliasLower = emailAlias.toLowerCase()
    const isEcommerce = config.ecommerceKeywords.some(keyword => 
      emailAliasLower.includes(keyword.toLowerCase())
    )
    
    const emailType = isEcommerce ? config.ecommerceSuffix : config.newsletterSuffix
    
    return config.template
      .replace('{subject}', subject)
      .replace('{month}', currentMonth)
      .replace('{year}', currentYear)
      .replace('{type}', emailType)
  }
  
  return {
    generateCampaignName,
    getDefaultCampaignConfig,
    validateCampaignConfig,
    previewCampaignName
  }
}